import React, { useEffect, useMemo, useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip,
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, ReferenceLine, Cell,
} from 'recharts';
import {
  Loader2, History, RotateCcw, Sparkles, Brain, Users, Lightbulb, Target,
  Compass, Award, Gauge, MessageSquare, Map as MapIcon, Download,
} from 'lucide-react';
import { atlasApi, AtlasUser, DiscColaborador, DiscFator, DiscPerfil, DiscAnalise } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  funccodi?: number;          // se omitido, usa o próprio do user (lookup por email)
  candidato?: { vagaId: string; candId: string };   // candidato de vaga (resultado fica no card)
  onRefazer?: () => void;
}

const DISC_CORES: Record<DiscFator, string> = { D: '#e11d48', I: '#f59e0b', S: '#10b981', C: '#3b82f6' };
const DISC_LABEL: Record<DiscFator, string> = { D: 'Dominância', I: 'Influência', S: 'Estabilidade', C: 'Conformidade' };
const FATORES: DiscFator[] = ['D', 'I', 'S', 'C'];

function formatarData(s?: string | null): string {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
}

// Cor por nível qualitativo.
// "Extremamente Alto/Baixo" só aparece em laudo importado do Sólides, que usa
// dois níveis a mais que o cálculo nativo do Atlas.
function nivelCor(nivel: string): string {
  const n = (nivel || '').toLowerCase();
  if (n.includes('extremamente alt')) return '#047857';
  if (n.includes('muito alt')) return '#059669';
  if (n.includes('normal alt')) return '#84cc16';
  if (n.startsWith('alt')) return '#10b981';
  if (n.includes('normal baix')) return '#f97316';
  if (n.includes('extremamente baix')) return '#9f1239';
  if (n.includes('muito baix')) return '#e11d48';
  if (n.startsWith('baix')) return '#fb7185';
  return '#f59e0b'; // Normal
}

// Barra horizontal com rótulo de nível
const LinhaNivel: React.FC<{ nome: string; valor: number; nivel: string; idx?: number }> = ({ nome, valor, nivel, idx }) => {
  const cor = nivelCor(nivel);
  return (
    <div className="flex items-center gap-3 py-1.5">
      {idx !== undefined && (
        <span className="w-5 text-[11px] text-gray-300 font-bold text-right flex-shrink-0">{idx}</span>
      )}
      <span className="w-40 text-sm text-gray-700 truncate flex-shrink-0">{nome}</span>
      <div className="flex-1 h-2.5 rounded-full bg-gray-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(2, valor)}%`, backgroundColor: cor }} />
      </div>
      <span className="w-28 text-[11px] font-semibold text-right flex-shrink-0" style={{ color: cor }}>{nivel}</span>
    </div>
  );
};

const Secao: React.FC<{ id: string; titulo: string; icon: React.ReactNode; children: React.ReactNode }> = ({ id, titulo, icon, children }) => (
  <section id={id} className="bg-white rounded-2xl border border-gray-200 p-5 scroll-mt-24">
    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
      <span className="text-purple-500">{icon}</span> {titulo}
    </h3>
    {children}
  </section>
);

const RelatorioIndividual: React.FC<Props> = ({ user, funccodi, candidato, onRefazer }) => {
  const [dados, setDados] = useState<(DiscColaborador & { funccodi?: number | null }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [baixando, setBaixando] = useState(false);
  const [erroPdf, setErroPdf] = useState<string | null>(null);

  const baixarPdf = async () => {
    setBaixando(true);
    setErroPdf(null);
    try {
      const { blob, nomeArquivo } = candidato
        ? await atlasApi.discCandidatoPdf(user, candidato.vagaId, candidato.candId)
        : await atlasApi.discPdf(user, funccodi);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeArquivo;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setErroPdf(e?.message || 'Falha ao gerar o PDF');
    } finally {
      setBaixando(false);
    }
  };

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    const req = candidato
      ? atlasApi.discCandidatoRelatorio(user, candidato.vagaId, candidato.candId)
      : funccodi !== undefined
        ? atlasApi.discColaborador(user, funccodi)
        : atlasApi.discMeu(user);
    req
      .then((r: any) => { if (!cancel) setDados(r); })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar perfil'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, funccodi, candidato?.vagaId, candidato?.candId]);

  const perfil: DiscPerfil | null = dados?.perfil?.perfil || null;
  const analise: DiscAnalise | undefined = dados?.perfil?.analise;
  const historico = dados?.historico || [];

  const radarData = useMemo(() => perfil ? FATORES.map(f => ({
    fator: f, Natural: perfil.natural[f], Adaptado: perfil.adaptado[f],
  })) : [], [perfil]);

  const cerebroData = useMemo(() =>
    (analise?.competencias || []).map(c => ({ nome: c.nome, valor: c.valor })), [analise]);

  if (loading) {
    return (
      <div className="p-12 text-center text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando perfil...
      </div>
    );
  }

  if (erro) {
    return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700 max-w-2xl">{erro}</div>;
  }

  if (!perfil) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-8 max-w-2xl text-center">
        <p className="text-gray-500">Nenhum perfil DISC registrado ainda.</p>
        {onRefazer && (
          <button onClick={onRefazer} className="mt-4 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium">
            Fazer questionário
          </button>
        )}
      </div>
    );
  }

  const corPrincipal = DISC_CORES[perfil.estilo_principal];

  const atalhos = [
    analise?.perfil_predominante && { id: 'predominante', label: 'Perfil Predominante' },
    analise?.subcaracteristicas?.length && { id: 'subcaract', label: 'Subcaracterísticas' },
    analise && (analise.habilidades_basicas || analise.habilidades_comuns) && { id: 'habilidades', label: 'Habilidades' },
    analise?.fatores?.length && { id: 'fatores', label: 'Fatores +/−' },
    analise?.indicadores?.length && { id: 'indicadores', label: 'Indicadores' },
    { id: 'graficos', label: 'Gráficos' },
    analise?.estilo_lideranca && { id: 'lideranca', label: 'Liderança' },
    analise?.relacionamento && { id: 'relacionamento', label: 'Relacionamento' },
    analise?.tomada_decisao && { id: 'decisoes', label: 'Decisões' },
    analise?.competencias?.length && { id: 'competencias', label: 'Competências' },
    analise?.talentos?.length && { id: 'talentos', label: 'Área de Talentos' },
  ].filter(Boolean) as Array<{ id: string; label: string }>;

  const irPara = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="space-y-4 max-w-5xl">
      {/* Cabeçalho */}
      <div id="topo" className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-wrap items-center gap-4">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-3xl font-black flex-shrink-0"
          style={{ backgroundColor: corPrincipal }}
        >
          {perfil.estilo_principal}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Estilo predominante</div>
          <h2 className="text-xl font-bold text-gray-900">{DISC_LABEL[perfil.estilo_principal]}</h2>
          <p className="text-sm text-gray-600">{perfil.descricao_principal}</p>
          <p className="text-xs text-gray-400 mt-1">
            Secundário: {DISC_LABEL[perfil.estilo_secundario]} · respondido em {formatarData(dados?.perfil?.respondido_em)}
          </p>
          {dados?.perfil?.origem === 'solides' && (
            <p className="text-xs text-gray-500 mt-1.5">
              <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 font-medium">
                Profiler Sólides
              </span>{' '}
              Laudo importado — fatores e níveis são os do original; os textos descritivos são do Atlas.
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className="flex items-center gap-2">
            <button
              onClick={baixarPdf}
              disabled={baixando}
              className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-wait text-white text-sm font-medium flex items-center gap-2"
            >
              {baixando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {baixando ? 'Gerando...' : 'Baixar PDF'}
            </button>
            {onRefazer && (
              <button onClick={onRefazer} className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2">
                <RotateCcw className="w-4 h-4" /> Refazer
              </button>
            )}
          </div>
          {erroPdf && <span className="text-xs text-rose-600">{erroPdf}</span>}
        </div>
      </div>

      {/* Atalhos */}
      <div className="bg-white rounded-2xl border border-gray-200 px-3 py-2 sticky top-2 z-10 overflow-x-auto">
        <div className="flex items-center gap-1.5 min-w-max">
          {atalhos.map(a => (
            <button
              key={a.id}
              onClick={() => irPara(a.id)}
              className="px-3 py-1.5 rounded-full text-xs font-medium text-gray-600 hover:bg-purple-50 hover:text-purple-700 transition whitespace-nowrap"
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Perfil predominante */}
      {analise?.perfil_predominante && (
        <Secao id="predominante" titulo="Perfil Predominante" icon={<Sparkles className="w-4 h-4" />}>
          <p className="text-sm text-gray-700 leading-relaxed">{analise.perfil_predominante}</p>
        </Secao>
      )}

      {/* Subcaracterísticas */}
      {analise?.subcaracteristicas?.length ? (
        <Secao id="subcaract" titulo="Subcaracterísticas" icon={<Compass className="w-4 h-4" />}>
          <div className="flex flex-wrap gap-2">
            {analise.subcaracteristicas.map(s => (
              <span key={s} className="px-3 py-1.5 rounded-full text-sm font-medium bg-purple-50 text-purple-700 border border-purple-100">
                {s}
              </span>
            ))}
          </div>
        </Secao>
      ) : null}

      {/* Habilidades */}
      {analise && (analise.habilidades_basicas || analise.habilidades_comuns) && (
        <Secao id="habilidades" titulo="Habilidades" icon={<Lightbulb className="w-4 h-4" />}>
          {analise.habilidades_basicas && (
            <div className="mb-3">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Habilidades básicas</h4>
              <p className="text-sm text-gray-700 leading-relaxed">{analise.habilidades_basicas}</p>
            </div>
          )}
          {analise.habilidades_comuns && (
            <div>
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Habilidades comuns</h4>
              <p className="text-sm text-gray-700 leading-relaxed">{analise.habilidades_comuns}</p>
            </div>
          )}
        </Secao>
      )}

      {/* Fatores positivos e negativos */}
      {analise?.fatores?.length ? (
        <Secao id="fatores" titulo="Fatores positivos e negativos" icon={<Gauge className="w-4 h-4" />}>
          <div className="space-y-3">
            {analise.fatores.map(f => (
              <div key={f.fator}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="inline-flex items-center gap-2 font-medium text-gray-700">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: DISC_CORES[f.fator] }} />
                    {f.label}
                  </span>
                  <span className="font-semibold">
                    <span className="text-emerald-600">+{f.positivo}%</span>{' '}
                    <span className="text-gray-300">·</span>{' '}
                    <span className="text-rose-500">−{f.negativo}%</span>
                  </span>
                </div>
                <div className="flex h-2.5 rounded-full overflow-hidden bg-gray-100">
                  <div className="h-full bg-emerald-500" style={{ width: `${f.positivo}%` }} />
                  <div className="h-full bg-rose-400" style={{ width: `${f.negativo}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Secao>
      ) : null}

      {/* Indicadores situacionais */}
      {analise?.indicadores?.length ? (
        <Secao id="indicadores" titulo="Indicadores situacionais" icon={<Gauge className="w-4 h-4" />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            {analise.indicadores.map(i => (
              <LinhaNivel key={i.nome} nome={i.nome} valor={i.valor} nivel={i.nivel} />
            ))}
          </div>
        </Secao>
      ) : null}

      {/* Gráficos */}
      <Secao id="graficos" titulo="Gráficos" icon={<Brain className="w-4 h-4" />}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 text-center">DISC · Natural x Adaptado</h4>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData} outerRadius="70%">
                <PolarGrid />
                <PolarAngleAxis dataKey="fator" tick={{ fontSize: 13, fontWeight: 700 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Radar name="Natural" dataKey="Natural" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.25} />
                <Radar name="Adaptado" dataKey="Adaptado" stroke="#10b981" fill="#10b981" fillOpacity={0.2} />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {cerebroData.length > 0 && (
            <div>
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 text-center">Mapa de competências</h4>
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={cerebroData} outerRadius="78%">
                  <PolarGrid gridType="polygon" />
                  <PolarAngleAxis dataKey="nome" tick={{ fontSize: 8, fill: '#6b7280' }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar name="Competência" dataKey="valor" stroke="#a855f7" fill="#a855f7" fillOpacity={0.3} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Perfil isolado */}
        {analise?.perfil_isolado?.length ? (
          <div className="mt-6">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Perfil isolado</h4>
            <div className="space-y-2">
              {analise.perfil_isolado.map(p => {
                const pct = Math.min(50, Math.abs(p.valor) / 25 * 50);
                const positivo = p.valor >= 0;
                return (
                  <div key={p.nome} className="flex items-center gap-3">
                    <span className="w-24 text-sm text-gray-700 truncate flex-shrink-0">{p.nome}</span>
                    <div className="flex-1 flex items-center h-5">
                      <div className="w-1/2 flex justify-end">
                        {!positivo && (
                          <div className="h-3 rounded-l-full" style={{ width: `${pct}%`, backgroundColor: DISC_CORES[p.fator] }} />
                        )}
                      </div>
                      <div className="w-px h-5 bg-gray-300" />
                      <div className="w-1/2 flex justify-start">
                        {positivo && (
                          <div className="h-3 rounded-r-full" style={{ width: `${pct}%`, backgroundColor: DISC_CORES[p.fator] }} />
                        )}
                      </div>
                    </div>
                    <span className="w-10 text-[11px] font-semibold text-right flex-shrink-0" style={{ color: DISC_CORES[p.fator] }}>
                      {p.valor > 0 ? '+' : ''}{p.valor}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </Secao>

      {/* Estilo de liderança */}
      {analise?.estilo_lideranca && (
        <Secao id="lideranca" titulo="Estilo de liderança" icon={<Award className="w-4 h-4" />}>
          <p className="text-sm text-gray-700 leading-relaxed">{analise.estilo_lideranca}</p>
        </Secao>
      )}

      {/* Relacionando-se com os outros */}
      {analise?.relacionamento && (
        <Secao id="relacionamento" titulo="Relacionando-se com os outros" icon={<Users className="w-4 h-4" />}>
          <p className="text-sm text-gray-700 leading-relaxed">{analise.relacionamento}</p>
        </Secao>
      )}

      {/* Tomando decisões */}
      {analise?.tomada_decisao && (
        <Secao id="decisoes" titulo="Tomando decisões" icon={<MessageSquare className="w-4 h-4" />}>
          <p className="text-sm text-gray-700 leading-relaxed">{analise.tomada_decisao}</p>
        </Secao>
      )}

      {/* Competências */}
      {analise?.competencias?.length ? (
        <Secao id="competencias" titulo="Competências" icon={<Target className="w-4 h-4" />}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            {analise.competencias.map((c, i) => (
              <LinhaNivel key={c.nome} idx={i + 1} nome={c.nome} valor={c.valor} nivel={c.nivel} />
            ))}
          </div>
        </Secao>
      ) : null}

      {/* Área de Talentos */}
      {analise?.talentos?.length ? (
        <Secao id="talentos" titulo="Área de Talentos" icon={<MapIcon className="w-4 h-4" />}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="relative">
              <ResponsiveContainer width="100%" height={320}>
                <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                  <XAxis type="number" dataKey="x" domain={[-1.2, 1.2]} hide />
                  <YAxis type="number" dataKey="y" domain={[-1.2, 1.2]} hide />
                  <ZAxis type="number" dataKey="valor" range={[60, 400]} />
                  <ReferenceLine x={0} stroke="#d1d5db" />
                  <ReferenceLine y={0} stroke="#d1d5db" />
                  <Tooltip
                    cursor={{ strokeDasharray: '3 3' }}
                    formatter={(_v: any, _n: any, p: any) => [`${p.payload.nome}: ${p.payload.valor}%`, '']}
                  />
                  <Scatter data={analise.talentos} fill="#a855f7">
                    {analise.talentos.map((t, i) => (
                      <Cell key={i} fill={t.valor >= 60 ? '#a855f7' : '#cbd5e1'} fillOpacity={t.valor >= 60 ? 0.9 : 0.5} />
                    ))}
                  </Scatter>
                  {analise.posicao && (
                    <Scatter data={[{ ...analise.posicao, valor: 100, nome: 'Você' }]} fill="#e11d48" shape="star" />
                  )}
                </ScatterChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 text-[10px] font-bold text-gray-400 uppercase">
                <span className="absolute top-1 left-1/2 -translate-x-1/2">Emoção</span>
                <span className="absolute bottom-1 left-1/2 -translate-x-1/2">Razão</span>
                <span className="absolute left-1 top-1/2 -translate-y-1/2">Introv.</span>
                <span className="absolute right-1 top-1/2 -translate-y-1/2">Extrov.</span>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Melhores áreas de atuação</h4>
              <ol className="space-y-1">
                {analise.talentos.map((t, i) => (
                  <li key={t.nome} className="flex items-center gap-3 text-sm">
                    <span className="w-5 text-[11px] text-gray-300 font-bold text-right">{i + 1}</span>
                    <span className="flex-1 text-gray-700">{t.nome}</span>
                    <span className="text-[11px] font-semibold" style={{ color: nivelCor(t.nivel) }}>{t.nivel}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </Secao>
      ) : null}

      {/* Histórico */}
      {historico.length > 0 && (
        <Secao id="historico" titulo="Histórico" icon={<History className="w-4 h-4" />}>
          <ul className="space-y-1">
            {historico.map((h, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                <span
                  className="w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
                  style={{ backgroundColor: h.estilo_principal ? DISC_CORES[h.estilo_principal] : '#9ca3af' }}
                >
                  {h.estilo_principal || '?'}
                </span>
                Respondido em {formatarData(h.respondido_em)} → {h.estilo_principal ? DISC_LABEL[h.estilo_principal] : '—'}
              </li>
            ))}
          </ul>
        </Secao>
      )}
    </div>
  );
};

export default RelatorioIndividual;
